import { z } from 'zod';

import { DISCIPLINE_EVENT_TYPES } from '../enums';
import { AccountIdSchema } from './account';
import { REVIEW_MODES } from './review';

export const TRADING_NEXT_ACTION_TYPES = [
  'EXIT_REQUIRED',
  'SET_STOP',
  'REDUCE_POSITION_RISK',
  'RESTORE_WIDENED_STOP',
  'RAISE_STOP_BREAKEVEN',
  'REDUCE_POSITION_SIZE',
  'TAKE_TP1',
  'TAKE_TP2',
  'TAKE_TP3',
  'RECONCILE_HISTORY',
  'RECONCILE_CONTRACT',
  'RECONCILE_EXECUTION',
  'COMPLETE_REQUIRED_REVIEW',
  'HOLD_TO_TP1',
  'HOLD_TO_TP2',
  'HOLD_TO_TP3',
  'RUNNER',
] as const;

export const TRADING_NEXT_ACTION_PRIORITIES = ['CRITICAL', 'WARNING', 'REVIEW', 'INFO'] as const;

export const TRADING_NEXT_ACTION_CATEGORIES = [
  'CAPITAL_PROTECTION',
  'PLAN_COMPLIANCE',
  'DATA_QUALITY',
  'REVIEW_WORKFLOW',
] as const;

export const TradingNextActionTypeSchema = z.enum(TRADING_NEXT_ACTION_TYPES);
export const TradingNextActionPrioritySchema = z.enum(TRADING_NEXT_ACTION_PRIORITIES);
export const TradingNextActionCategorySchema = z.enum(TRADING_NEXT_ACTION_CATEGORIES);

export const TradingNextActionDestinationSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('POSITION_LIFECYCLE') }),
  z.object({ kind: z.literal('POSITION_STOP_EDITOR') }),
  z.object({ kind: z.literal('IMPORT_HISTORY') }),
  z.object({ kind: z.literal('TRADE_REVIEW'), tradeId: z.string().trim().min(1) }),
]);

export const TradingNextActionSourceReferenceSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('POSITION_EPISODE'),
    openingExecutionId: z.string().trim().min(1),
  }),
  z.object({
    kind: z.literal('POSITION_HISTORY_STATE'),
    symbol: z.string().trim().min(1),
    state: z.literal('HISTORY_INCOMPLETE'),
  }),
  z.object({ kind: z.literal('FROZEN_TRADE_PLAN'), tradePlanId: z.string().trim().min(1) }),
  z.object({
    kind: z.literal('CONTRACT_BINDING'),
    tradePlanId: z.string().trim().min(1),
    executionState: z.enum(['ON_PLAN', 'PLANNED_BUILD', 'ADD_DRIFT', 'REENTRY_DRIFT']),
  }),
  z.object({ kind: z.literal('EXECUTION'), tradeId: z.string().trim().min(1) }),
  z.object({ kind: z.literal('STOP_CHANGE'), stopChangeId: z.string().trim().min(1) }),
  z.object({
    kind: z.literal('DISCIPLINE_EVENT'),
    disciplineEventId: z.string().trim().min(1),
    eventType: z.enum(DISCIPLINE_EVENT_TYPES),
  }),
  z.object({
    kind: z.literal('PENDING_TRADE_REVIEW'),
    tradeId: z.string().trim().min(1),
    reviewMode: z.enum(REVIEW_MODES),
    reviewDueAt: z.string().datetime().nullable(),
  }),
]);

export const TradingNextActionQuerySchema = z.object({
  accountId: AccountIdSchema,
});

export const TradingNextActionDtoSchema = z.object({
  key: z.string().trim().min(1),
  type: TradingNextActionTypeSchema,
  priority: TradingNextActionPrioritySchema,
  category: TradingNextActionCategorySchema,
  account: z.object({
    id: z.string().trim().min(1),
    name: z.string(),
    currency: z.string().trim().min(1),
  }),
  symbol: z.string().trim().min(1),
  title: z.string().trim().min(1),
  reason: z.string().trim().min(1),
  suggestedAction: z.string().trim().min(1),
  destination: TradingNextActionDestinationSchema,
  sourceReferences: z.array(TradingNextActionSourceReferenceSchema).min(1),
});

export const TradingNextActionsResponseSchema = z.object({
  tradingNextActions: z.array(TradingNextActionDtoSchema),
});

export type TradingNextActionType = z.infer<typeof TradingNextActionTypeSchema>;
export type TradingNextActionPriority = z.infer<typeof TradingNextActionPrioritySchema>;
export type TradingNextActionCategory = z.infer<typeof TradingNextActionCategorySchema>;
export type TradingNextActionDestination = z.infer<typeof TradingNextActionDestinationSchema>;
export type TradingNextActionSourceReference = z.infer<
  typeof TradingNextActionSourceReferenceSchema
>;
export type TradingNextActionDto = z.infer<typeof TradingNextActionDtoSchema>;
export type TradingNextActionsResponse = z.infer<typeof TradingNextActionsResponseSchema>;
